import { useCallback, useEffect, useRef, useState } from 'react'

import { qrLoginCancel, qrLoginStart } from 'src/features/auth/api/qrLogin'

import { QR_PANEL_STATUS } from '../constants'
import type { QrPanelState, QrStartSession, QrStartSessionMode } from '../types'
import { getStartBody, isCancelableStatus } from '../utils'

type UseQrSessionLifecycleParams = {
  domainName?: string
  showApiErrors: (error: unknown) => void
}

export const useQrSessionLifecycle = ({
  domainName,
  showApiErrors
}: UseQrSessionLifecycleParams) => {
  const [state, setState] = useState<QrPanelState>({
    status: QR_PANEL_STATUS.STARTING,
    session: null
  })
  const [autoRenewCount, setAutoRenewCount] = useState(0)
  const [expiresAtMs, setExpiresAtMs] = useState<number | null>(null)
  const stateRef = useRef(state)
  const startRequestIdRef = useRef(0)
  const handledApprovalRef = useRef(false)
  const renewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    stateRef.current = state
  }, [state])

  const clearScheduledWork = useCallback(() => {
    if (renewTimeoutRef.current) {
      clearTimeout(renewTimeoutRef.current)
      renewTimeoutRef.current = null
    }
    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current)
      refetchTimeoutRef.current = null
    }
  }, [])

  const startSession: QrStartSession = useCallback(
    async (renewMode: QrStartSessionMode = 'manual') => {
      const requestId = startRequestIdRef.current + 1
      startRequestIdRef.current = requestId

      clearScheduledWork()
      handledApprovalRef.current = false
      setState({ status: QR_PANEL_STATUS.STARTING, session: null })

      try {
        const session = await qrLoginStart(getStartBody(domainName))
        if (startRequestIdRef.current !== requestId) {
          return
        }

        setState({ status: QR_PANEL_STATUS.ACTIVE, session })
        setExpiresAtMs(Date.now() + session.expires_in * 1000)
        setAutoRenewCount((count) => (renewMode === 'auto' ? count + 1 : 0))
      } catch (error) {
        if (startRequestIdRef.current !== requestId) {
          return
        }

        setState({ status: QR_PANEL_STATUS.ERROR, session: null })
        showApiErrors(error)
      }
    },
    [clearScheduledWork, domainName, showApiErrors]
  )

  const cancelCurrentSession = useCallback(async () => {
    const current = stateRef.current
    if (!current.session || !isCancelableStatus(current.status)) {
      return
    }

    try {
      await qrLoginCancel({
        session_id: current.session.session_id,
        web_token: current.session.web_token
      })
    } catch {
      // Cancel is intentionally best-effort; the session will expire server-side.
    }
  }, [])

  useEffect(() => {
    const startTimer = setTimeout(() => {
      startSession()
    }, 0)

    return () => {
      clearTimeout(startTimer)
      clearScheduledWork()
      startRequestIdRef.current += 1
      cancelCurrentSession()
    }
  }, [cancelCurrentSession, clearScheduledWork, startSession])

  return {
    state,
    stateRef,
    setState,
    autoRenewCount,
    expiresAtMs,
    setExpiresAtMs,
    handledApprovalRef,
    renewTimeoutRef,
    refetchTimeoutRef,
    startSession,
    cancelCurrentSession
  }
}
